import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import api from 'common/api';
import { selectBundleTabPage } from 'common/selectors';
import { openNewBackgroundTab } from 'utils/browser';
import Logger from 'utils/logger';

export default function useDocFinderNavigation(caseId: string) {
  const { addToast } = useToasts();
  const isBundleTabPage = useSelector(selectBundleTabPage);
  const navigatingRef = useRef(false);

  const navigateToDocRef = useCallback(
    async (refText: string) => {
      if (navigatingRef.current || !caseId) return;
      navigatingRef.current = true;

      try {
        const res: any = await api.get(
          `/cases/${caseId}/doc-finder?search=${encodeURIComponent(refText)}`,
        );

        if (res && res.error) {
          Logger.ERROR('Doc-finder API error', res.error);
          addToast('Could not find the data to navigate', {
            appearance: 'warning',
            autoDismiss: true,
          });
          return;
        }

        const results = Array.isArray(res) ? res : res?.results;
        if (!results || results.length === 0) {
          addToast('Could not find the data to navigate', {
            appearance: 'warning',
            autoDismiss: true,
          });
          return;
        }

        const doc = results[0];
        const params = new URLSearchParams();

        let effectiveStartPage = doc.startPage;
        if (!isBundleTabPage) {
          if (doc.bundleLocations?.length > 0 && doc.bundleLocations[0].startPage != null) {
            effectiveStartPage = doc.bundleLocations[0].startPage;
          } else {
            // Fallback: fetch file details to get startPage
            const detailsRes: any = await api.get(
              `/cases/${caseId}/files/${doc.id}/details?firstTrialBookOnly=true`,
            );
            if (detailsRes && !detailsRes.error && detailsRes.startPage != null) {
              effectiveStartPage = detailsRes.startPage;
            } else {
              effectiveStartPage = null;
            }
          }
        }

        // For slash-separated refs like H4/6/3, the number after the last slash is the page
        const lastSlashIdx = refText.lastIndexOf('/');
        if (lastSlashIdx !== -1) {
          const pageStr = refText.slice(lastSlashIdx + 1).trim();
          if (pageStr && /^\d+$/.test(pageStr)) {
            params.set('goToPage', pageStr);
          }
        } else if (doc.globalPagePrefix != null && effectiveStartPage != null) {
          // For non-slash refs like C117: truncate the globalPagePrefix, then goToPage = page - startPage + 1
          const prefix = String(doc.globalPagePrefix);
          if (refText.startsWith(prefix)) {
            const pageNum = parseInt(refText.slice(prefix.length), 10);
            const startPage = parseInt(String(effectiveStartPage), 10);
            if (!isNaN(pageNum) && !isNaN(startPage)) {
              params.set('goToPage', String(pageNum - startPage + 1));
            }
          }
        }

        if (doc.tab != null) params.set('tab', String(doc.tab));
        if (effectiveStartPage != null) params.set('startPage', String(effectiveStartPage));
        if (doc.pageCount != null) params.set('pageCount', String(doc.pageCount));
        if (doc.globalPagePrefix != null)
          params.set('globalPagePrefix', String(doc.globalPagePrefix));

        const query = params.toString();
        const url = `/case/${caseId}/files/${doc.id}${query ? `?${query}` : ''}`;
        openNewBackgroundTab(url);
      } catch (error) {
        Logger.ERROR('Doc-finder navigation failed', error);
        addToast('Could not find the data to navigate', {
          appearance: 'warning',
          autoDismiss: true,
        });
      } finally {
        navigatingRef.current = false;
      }
    },
    [caseId, addToast, isBundleTabPage],
  );

  return { navigateToDocRef };
}
